import { fetchCustomerData } from "@/redux/slices/customerSlice";
import { fetchVoucherByCode, setQrCode, clearVoucher } from "@/redux/slices/qrSlice";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import "swiper/css";
import { useNavigate } from "react-router-dom";
import { Loader2, QrCode, UserRound } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useRef, useState } from "react";
import { assignVoucherdata, transactions } from "@/services/customer";

import { getT } from '../i18n/translations';
import ScratchCard from "./ScratchCard";

export default function Home() {
  const t = getT();
  const name = localStorage.getItem("name");
  const mobile = localStorage.getItem("mobile") || localStorage.getItem("phone") || localStorage.getItem("phoneNumber") || "";
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showCodePopup, setShowCodePopup] = useState(false);
  const [manualCode, setManualCode] = useState("");
  const [status, setStatus] = useState("");
  const [message, setMessage] = useState("");
  const [voucherValue, setVoucherValue] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [rewardClaimed, setRewardClaimed] = useState(false);
  const [rewardAmount, setRewardAmount] = useState(0);
  const [scratchLoader, setScratchLoader] = useState(false)
  const [isScratch, setIsScratch] = useState(false)

  const { data: customer, loading } = useSelector((state) => state.customer);
  const { code: qrCode, voucher, loading: voucherLoading } = useSelector((state) => state.qr);
  const token = useSelector((state) => state.auth.token);
  const lastProcessedCode = useRef(null);

  const getInitials = (name) => {
    if (!name) return "";
    const words = name.trim().split(" ");
    return words.length === 1 ? words[0][0] : words[0][0] + words[1][0];
  };

  useEffect(() => {
    const customerCode = localStorage.getItem("customercode");
    dispatch(fetchCustomerData(customerCode));
  }, []);

  useEffect(() => {
    if (!qrCode || !token) return;
    lastProcessedCode.current = null;
    dispatch(clearVoucher());
    dispatch(fetchVoucherByCode({ token, code: qrCode }));
  }, [qrCode, token]);

  useEffect(() => {
    if (!voucher || !qrCode || !customer) return;
    if (lastProcessedCode.current === qrCode) return;
    lastProcessedCode.current = qrCode;

    const data = voucher?.data?.voucher;

    if (!data) { setStatus("error"); setMessage(t.home_errInvalid); setShowPopup(true); return; }
    if (data?.voucherUsed === true) { setStatus("error"); setMessage(t.home_errUsed); setShowPopup(true); return; }
    const currentWallet = customer?.WalletSummary ?? [];
    const totalVouchers = currentWallet.filter((item) => item.type === "voucher").length;

    if (totalVouchers >= 10) {
      setStatus("error");
      setMessage(t.home_errMaxLimit);
      setShowPopup(true);
      return;
    }

    if (data?.isAssigned === true) {
      const alreadyMine = currentWallet.some(
        (item) => item.voucherCode === data?.code || item.voucherCode === qrCode
      );
      if (alreadyMine) {
        setStatus("already_mine");
        setMessage(t.home_errAssignedMine);
        setVoucherValue(data?.vouchervalue ?? 0);
        setShowPopup(true);
        return;
      } else {
        setStatus("error");
        setMessage(t.home_errAssignedOther);
        setShowPopup(true);
        return;
      }
    }

    if (new Date(data?.expiryDate) < new Date()) { setStatus("error"); setMessage(t.home_errExpired); setShowPopup(true); return; }

    const value = data?.vouchervalue ?? 0;
    setVoucherValue(value);


    assignVoucherdata(token, {
      client: "MER000499",
      voucherTemplate: data?.voucherTemplateCode,
      voucher: data?.code,
      Value: value,
      CustomerCode: localStorage.getItem("customercode"),
    }).then(() => {
      const customerCode = localStorage.getItem("customercode");
      dispatch(fetchCustomerData(customerCode));
    });


    setStatus("success");
    setMessage(t.home_errSuccess);
    setShowPopup(true);
  }, [voucher, qrCode, customer]);
  const handleClosePopup = () => {
    setShowPopup(false);
    dispatch(setQrCode(null));
    localStorage.removeItem("qrCode");
  };

  const handleScanAnother = () => {
    setShowPopup(false);
    dispatch(setQrCode(null));
    localStorage.removeItem("qrCode");
    navigate("/scan");
  };

  const getRandomReward = () => {
    return Math.floor(Math.random() * (60 - 5 + 1)) + 5;
  };

  const handleManualSubmit = () => {
    const code = manualCode.trim().toUpperCase();
    if (!code) return;
    dispatch(setQrCode(null));
    requestAnimationFrame(() => { dispatch(setQrCode(code)); });
    setShowCodePopup(false);
    setManualCode("");
  };

  const handleScratchStart = () => {
    let reward = rewardAmount;

    // generate only once
    if (reward === 0) {
      reward = getRandomReward();
      setRewardAmount(reward);
    }

    return reward;
  };

  const handleScratchComplete = async (reward) => {
    if (rewardClaimed) return;

    setRewardClaimed(true);
    setScratchLoader(true);

    try {
      const mobile = localStorage.getItem("mobile");

      const body = {
        activityType: "Games",
        amount: reward, // ✅ use direct reward
        mobile: mobile?.replace(/^\+91/, ""),
        schemacode: "TSC000337",
      };


      const token = JSON.parse(localStorage.getItem("token_gen"));

      const res = await transactions(token?.access_token, body);

      if (res?.message === "Request processed successfully.") {
        const customerCode = localStorage.getItem("customercode");

        dispatch(fetchCustomerData(customerCode));

        setStatus("success");
        setMessage("Reward claimed successfully");
        setIsScratch(true);
      }
    } catch (error) {
      setStatus("error");
      setMessage("Failed to claim reward");
    } finally {
      setScratchLoader(false);
    }
  };

  return (
    <div
      className="h-screen flex justify-center overflow-hidden"
      style={{
        fontFamily: "'Poppins', sans-serif",
        backgroundColor: "#fff5f5",
        backgroundImage:
          "radial-gradient(circle, #ffd6d6 1px, transparent 1px)",
        backgroundSize: "22px 22px",
      }}
    >
      <div className="w-full max-w-md h-full flex flex-col overflow-y-auto overflow-x-hidden bg-[#ffe5e5]">

        {/* LOADER */}
        {(loading || voucherLoading || scratchLoader) && (
          <div className="fixed inset-0 bg-black/30 flex justify-center items-center z-50 backdrop-blur-sm">
            <Loader2 className="animate-spin h-12 w-12 text-[#ED1C16]" />
          </div>
        )}

        <div className="px-4 pt-5 pb-6 space-y-4 flex-1 overflow-x-hidden">

          {/* HEADER CARD */}
          <div
            className="rounded-3xl p-6 relative overflow-hidden min-h-[260px]"
            style={{
              background:
                "linear-gradient(160deg, #ED1C16 0%, #111 60%, #B30000 100%)",
            }}
          >
            <div className="flex justify-between items-start mb-4 relative z-10">
              <div>
                <p className="text-[11px] font-semibold tracking-widest text-white/80 uppercase">
                  {t.home_welcome}
                </p>
                <p className="text-[22px] font-bold text-white leading-tight mt-1">
                  {customer?.customer?.name}
                </p>
              </div>

              <div
                onClick={() => navigate("/profile")}
                className="w-11 h-11 rounded-full bg-white/15 flex items-center justify-center cursor-pointer active:scale-95 border border-white/20"
              >
                <UserRound className="w-5 h-5 text-white" />
              </div>
            </div>

            <div
              className="rounded-2xl p-5 mt-4"
              style={{
                backgroundColor: "rgba(0,0,0,0.25)",
                border: "1px solid rgba(255,255,255,0.15)",
              }}
            >
              <div className="grid grid-cols-3 gap-4 mb-5">
                {[
                  {
                    label: t.home_totalEarned,
                    value: `₹${customer?.walletsDetail?.earnedValue || 0}`,
                  },
                  { label: "", value: "" },
                  {
                    label: t.home_balance,
                    value: `₹${customer?.walletsDetail?.availableValue || 0}`,
                  },
                ].map(({ label, value }) => (
                  <div key={label} className="text-center">
                    <p className="text-[9px] font-semibold tracking-wider text-white/70 uppercase mb-1">
                      {label}
                    </p>
                    <p className="text-[17px] font-bold text-white">{value}</p>
                  </div>
                ))}
              </div>

              <div className="flex gap-3 mb-3">
                <button
                  onClick={() => navigate("/redeem-cash")}
                  className="flex-1 py-3 rounded-2xl text-sm font-semibold bg-black/30 text-white border border-white/20"
                >
                  {t.home_cash}
                </button>

                <button
                  onClick={() => navigate("/gift-card")}
                  className="flex-1 py-3 rounded-2xl text-sm font-semibold bg-black/30 text-white border border-white/20"
                >
                  {t.home_giftcard}
                </button>
              </div>

              <button
                onClick={() => navigate("/history")}
                className="w-full py-3 rounded-2xl text-sm font-semibold bg-white/10 text-white border border-white/20"
              >
                {t.home_viewEarnings}
              </button>
            </div>
          </div>

          {/* GAMES */}
          <div className="space-y-3">
            <h2 className="text-[18px] font-bold text-black">
              {t.home_gamesTitle}
            </h2>

            <Swiper
              modules={[Autoplay]}
              spaceBetween={14}
              slidesPerView={1.25}
              autoplay={{ delay: 2800, disableOnInteraction: false }}
              loop
              className="!pb-2"
            >
              {/* SPIN THE WHEEL */}
              <SwiperSlide>
                <div
                  onClick={() => navigate("/spin")}
                  className="relative h-[210px] rounded-[28px] overflow-hidden cursor-pointer shadow-2xl active:scale-[0.97] transition-all duration-300"
                  style={{
                    background:
                      "linear-gradient(145deg, #ED1C16 0%, #B30000 45%, #111111 100%)",
                  }}
                >
                  {/* Glow */}
                  <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/10 rounded-full blur-3xl" />

                  {/* Decorative circles */}
                  <div className="absolute top-3 right-3 w-20 h-20 border border-white/10 rounded-full" />
                  <div className="absolute bottom-4 left-4 w-14 h-14 border border-white/10 rounded-full" />

                  {/* Floating wheel icon */}
                  <div className="absolute right-[-10px] bottom-[-12px] opacity-20">
                    <div className="w-36 h-36 rounded-full border-[10px] border-white animate-spin duration-[7000ms]" />
                  </div>

                  <div className="relative z-10 flex flex-col h-full p-5 text-white">
                    <div className="flex items-center gap-2">
                      <span className="px-3 py-1 rounded-full bg-white/15 backdrop-blur text-[10px] uppercase tracking-wider font-semibold">
                        Lucky Game
                      </span>
                    </div>

                    <div className="mt-4">
                      <h3 className="text-[24px] font-black leading-none">
                        SPIN  & WIN
                      </h3>

                      <p className="text-[12px] mt-2 text-white/80 leading-relaxed max-w-[180px]">
                        Spin the wheel and unlock instant cashback rewards.
                      </p>
                    </div>

                    <div className="mt-auto flex items-center justify-between">
                      <button className="px-4 py-2 rounded-xl bg-white text-[#ED1C16] text-[12px] font-bold shadow-lg">
                        Play Now →
                      </button>

                      <div className="flex -space-x-2">
                        {[1, 2, 3].map((i) => (
                          <div
                            key={i}
                            className="w-7 h-7 rounded-full border-2 border-white bg-yellow-400"
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </SwiperSlide>

              {/* SCRATCH CARD */}
              <SwiperSlide>
                <div
                  className="relative h-[210px] rounded-[28px] overflow-hidden shadow-2xl"
                  style={{
                    background:
                      "linear-gradient(145deg, #111111 0%, #ED1C16 55%, #7A0000 100%)",
                  }}
                >
                  {/* Metallic shine */}
                  <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-transparent" />

                  {/* Decorative glow */}
                  <div className="absolute top-[-30px] left-[-30px] w-40 h-40 bg-red-500/20 rounded-full blur-3xl" />

                  {/* Coins */}
                  <div className="absolute right-4 top-4 text-[28px] rotate-12">
                    💰
                  </div>

                  <div className="relative z-10 h-full p-5 flex flex-col">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="px-3 py-1 rounded-full bg-white/15 text-white text-[10px] uppercase tracking-wider font-semibold">
                          Instant Reward
                        </span>

                        <h3 className="text-[23px] font-black text-white mt-3 leading-none">
                          SCRATCH & EARN
                        </h3>

                      </div>

                      <div className="animate-bounce text-3xl">
                        🎁
                      </div>
                    </div>

                    <p className="text-[12px] text-white/75 mt-1 mb-1 max-w-[190px]">
                      Scratch the card to reveal your surprise reward instantly.
                    </p>

                    {/* Scratch Area */}
                    <div className="mt-auto  p-1 ">
                      <ScratchCard
                        isScratch={isScratch}
                        reward={rewardAmount}
                        onStart={handleScratchStart}
                        onReveal={handleScratchComplete}
                      />
                    </div>
                  </div>
                </div>
              </SwiperSlide>
            </Swiper>
          </div>

          {/* PROMO */}
          {/* <div className="bg-white rounded-2xl px-5 py-5 shadow-sm border border-gray-100"> */}
          <button
            onClick={() => navigate("/scan")}
            className="w-full py-3.5 rounded-xl text-white font-semibold"
            style={{ backgroundColor: "#ED1C16" }}
          >
            Scan QR
          </button>
          {/* </div> */}
        </div>

        {/* FOOTER */}
        {/* <div className="w-full py-3 border-t text-center text-black text-sm">
          © {new Date().getFullYear()} Consumer Journeys. All Rights Reserved.
        </div> */}

        {/* POPUPS (only color changes) */}
        {showPopup && (
          <div className="fixed inset-0 bg-black/55 backdrop-blur-sm flex items-center justify-center z-50 px-4">
            <div
              className="w-full max-w-sm rounded-[32px] overflow-hidden relative shadow-[0_25px_80px_rgba(0,0,0,0.45)]"
              style={{
                background:
                  "linear-gradient(180deg,#ffffff 0%,#fff5f5 100%)",
              }}
            >
              {/* TOP RED HEADER */}
              <div
                className="relative px-6 pt-7 pb-16 text-center overflow-hidden z-10"
                style={{
                  background:
                    "linear-gradient(135deg,#F40009 0%,#B30000 55%,#7A0000 100%)",
                }}
              >
                {/* GLOW EFFECTS */}
                <div className="absolute -top-10 -left-10 w-40 h-40 bg-white/10 rounded-full blur-3xl" />
                <div className="absolute top-0 right-0 w-32 h-32 bg-yellow-300/10 rounded-full blur-2xl" />

                {/* CLOSE */}
                <button
                  onClick={handleClosePopup}
                  className="absolute top-4 right-4 z-50 w-10 h-10 rounded-full bg-white/20 border border-white/20 text-white backdrop-blur flex items-center justify-center active:scale-95 transition-all"
                >
                  ✕
                </button>

                {/* SUCCESS / ERROR ICON */}
                <div className="relative z-10">
                  {status === "success" ||
                    status === "already_mine" ||
                    status === "assigned_another_user" ? (
                    <div className="w-24 h-24 rounded-full bg-white flex items-center justify-center mx-auto shadow-2xl mb-5">
                      <span className="text-5xl">
                        {voucherValue > 0 ? "🎉" : "🏆"}
                      </span>
                    </div>
                  ) : (
                    <div className="w-24 h-24 rounded-full bg-white flex items-center justify-center mx-auto shadow-2xl mb-5">
                      <span className="text-5xl text-[#F40009]">✖</span>
                    </div>
                  )}

                  <p className="text-white/80 text-[11px] uppercase tracking-[4px] font-semibold">
                    Coca-Cola Rewards
                  </p>

                  {/* TITLE */}
                  {status === "already_mine" ||
                    status === "assigned_another_user" ? (
                    <h2 className="text-[26px] font-black text-white mt-2">
                      Already Claimed
                    </h2>
                  ) : status === "success" ? (
                    voucherValue > 0 ? (
                      <>
                        <h2 className="text-[22px] font-black text-white mt-2">
                          Congratulations
                        </h2>

                        <div className="text-[52px] font-black text-yellow-300 leading-none mt-3 drop-shadow-lg">
                          ₹{voucherValue}
                        </div>

                        <p className="text-[14px] text-white/85 mt-3 leading-relaxed">
                          Instant cashback reward unlocked.
                        </p>
                      </>
                    ) : (
                      <>
                        <h2 className="text-[24px] font-black text-white mt-2">
                          You're Eligible 🎉
                        </h2>

                        <p className="text-[14px] text-white/85 mt-3 leading-relaxed">
                          Participate now and stand a chance to win exciting rewards.
                        </p>
                      </>
                    )
                  ) : (
                    <>
                      <h2 className="text-[24px] font-black text-white mt-2">
                        Verification Failed
                      </h2>

                      <p className="text-[14px] text-white/85 mt-3 leading-relaxed">
                        {message}
                      </p>
                    </>
                  )}
                </div>
              </div>

              {/* WHITE CONTENT */}
              <div className="relative px-5 pb-5 -mt-10 z-20">
                <div className="bg-white rounded-[28px] p-5 shadow-2xl border border-red-100">

                  {/* SUCCESS */}
                  {status === "success" ? (
                    <>
                      {voucherValue > 0 ? (
                        <>
                          <div className="bg-[#fff5f5] border border-[#ffd6d6] rounded-2xl p-4 text-center mb-4">
                            <p className="text-[13px] text-gray-500 leading-relaxed">
                              Cashback has been credited successfully to your wallet.
                            </p>
                          </div>

                          <button
                            onClick={() => {
                              handleClosePopup();
                              navigate("/redeem-cash");
                            }}
                            className="w-full py-3.5 rounded-2xl text-white font-bold text-[14px] active:scale-[0.98] transition-all"
                            style={{
                              background:
                                "linear-gradient(135deg,#F40009 0%,#B30000 100%)",
                            }}
                          >
                            View Earnings →
                          </button>

                          <button
                            onClick={handleScanAnother}
                            className="w-full py-3.5 rounded-2xl font-semibold text-[14px] border border-red-100 mt-3 text-[#B30000] bg-[#fff5f5]"
                          >
                            Scan Another Code
                          </button>

                          <button
                            onClick={() => {
                              handleClosePopup();
                              navigate("/slogan");
                            }}
                            className="w-full py-3.5 rounded-2xl font-bold text-[14px] mt-3 text-[#111]"
                            style={{
                              background:
                                "linear-gradient(135deg,#FFD84D 0%,#FFC107 100%)",
                            }}
                          >
                            🏆 Participate for Grand Prize
                          </button>
                        </>
                      ) : (
                        <>
                          <div className="bg-[#fff5f5] border border-[#ffd6d6] rounded-2xl p-4 text-center mb-4">
                            {customer?.customer?.slogan ? (
                              <p className="text-[13px] text-gray-500 leading-relaxed">
                                Your contest entry has already been received successfully.
                              </p>
                            ) : (
                              <p className="text-[13px] text-gray-500 leading-relaxed">
                                You are eligible to participate in the contest.
                              </p>
                            )}
                          </div>

                          {customer?.customer?.slogan ? (
                            <>
                              <button
                                onClick={() => {
                                  handleClosePopup();
                                  navigate("/scan");
                                }}
                                className="w-full py-3.5 rounded-2xl text-white font-bold text-[14px]"
                                style={{
                                  background:
                                    "linear-gradient(135deg,#F40009 0%,#B30000 100%)",
                                }}
                              >
                                Scan More Codes
                              </button>

                              <button
                                onClick={() => {
                                  handleClosePopup();
                                  navigate("/home");
                                }}
                                className="w-full py-3.5 rounded-2xl font-semibold text-[14px] border border-red-100 mt-3 text-[#B30000] bg-[#fff5f5]"
                              >
                                Go Home
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={() => {
                                handleClosePopup();
                                navigate("/slogan");
                              }}
                              className="w-full py-3.5 rounded-2xl text-white font-bold text-[14px]"
                              style={{
                                background:
                                  "linear-gradient(135deg,#F40009 0%,#B30000 100%)",
                              }}
                            >
                              Participate Now 🚀
                            </button>
                          )}
                        </>
                      )}
                    </>
                  ) : status === "already_mine" ||
                    status === "assigned_another_user" ? (
                    <>
                      <div className="bg-[#fff5f5] border border-[#ffd6d6] rounded-2xl p-4 text-center mb-4">
                        <p className="text-[13px] text-gray-500 leading-relaxed">
                          This QR code has already been assigned.
                        </p>
                      </div>

                      <button
                        onClick={() => {
                          handleClosePopup();
                          navigate("/scan");
                        }}
                        className="w-full py-3.5 rounded-2xl text-white font-bold text-[14px]"
                        style={{
                          background:
                            "linear-gradient(135deg,#F40009 0%,#B30000 100%)",
                        }}
                      >
                        Try Another Code
                      </button>
                    </>
                  ) : (
                    <>
                      <div className="bg-[#fff5f5] border border-[#ffd6d6] rounded-2xl p-4 text-center mb-4">
                        <p className="text-[13px] text-gray-500 leading-relaxed">
                          {message}
                        </p>
                      </div>

                      <button
                        onClick={handleClosePopup}
                        className="w-full py-3.5 rounded-2xl text-white font-bold text-[14px]"
                        style={{
                          background:
                            "linear-gradient(135deg,#F40009 0%,#B30000 100%)",
                        }}
                      >
                        Try Again
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}